"use client"

import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/util"

// This project has multiple @types/react versions (via hoist-non-react-statics),
// which makes ForwardRefExoticComponent not match across type boundaries.
// Cast Radix primitives to React.FC<any> so they can be used in JSX.
type AnyFC = React.FC<any> // eslint-disable-line

const PrimRoot = AccordionPrimitive.Root as unknown as AnyFC
const PrimItem = AccordionPrimitive.Item as unknown as AnyFC
const PrimHeader = AccordionPrimitive.Header as unknown as AnyFC
const PrimTrigger = AccordionPrimitive.Trigger as unknown as AnyFC
const PrimContent = AccordionPrimitive.Content as unknown as AnyFC

function Accordion(props: AccordionPrimitive.AccordionSingleProps | AccordionPrimitive.AccordionMultipleProps) {
  return <PrimRoot {...props} />
}
Accordion.displayName = "Accordion"

function AccordionItem({
  className,
  ...props
}: AccordionPrimitive.AccordionItemProps & { className?: string }) {
  return (
    <PrimItem
      className={cn("border-b", className)}
      {...props}
    />
  )
}
AccordionItem.displayName = "AccordionItem"

function AccordionTrigger({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLButtonElement> & { children?: React.ReactNode }) {
  return (
    <PrimHeader className="flex">
      <PrimTrigger
        className={cn(
          "flex flex-1 items-center justify-between py-4 font-medium transition-all [&[data-state=open]>svg]:rotate-180",
          className
        )}
        {...props}
      >
        {children}
        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
      </PrimTrigger>
    </PrimHeader>
  )
}
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

function AccordionContent({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { children?: React.ReactNode }) {
  return (
    <PrimContent
      className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn("pb-4 pt-0", className)}>{children}</div>
    </PrimContent>
  )
}
AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
